import React, { useState, useEffect, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import MetaTags from "react-meta-tags"
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Badge,
    Table
} from "reactstrap"
import classnames from "classnames"

import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import Flatpickr from "react-flatpickr"
import { get, post, put, uploadfile } from "helpers/api_helper"
import Notifications from "components/Notifications"
import {
    ASSET_URL,
    ASSET_TYPE_URL,
    ASSET_DETAILS_URL,
    ASSET_ASSIGN_URL,
    ASSET_RETURN_URL,
    ASSET_HISTORY_URL
} from "helpers/url_helper"
import "flatpickr/dist/themes/material_green.css"

const Assets = () => {
    const [activeTab, setActiveTab] = useState("2")
    const [assetTypeModal, setAssetTypeModal] = useState(false)
    const [assetModal, setAssetModal] = useState(false)
    const [editId, setEditId] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [viewAssetData, setViewAssetData] = useState(null)
    const [viewModal, setViewModal] = useState(false)
    const [existingImage, setExistingImage] = useState(null)

    // ─── Assign Modal State ──────────────────────────────────────────────────────
    const [assignModal, setAssignModal] = useState(false)
    const [assignAssetId, setAssignAssetId] = useState(null)
    const [assignAssetName, setAssignAssetName] = useState("")
    const [assignForm, setAssignForm] = useState({
        employee_id: "",
        assign_date: "",
        remark: ""
    })
    const [isAssignLoading, setIsAssignLoading] = useState(false)

    // ─── Return Modal State ──────────────────────────────────────────────────────
    const [returnModal, setReturnModal] = useState(false)
    const [returnAssetId, setReturnAssetId] = useState(null)
    const [returnAssetName, setReturnAssetName] = useState("")
    const [returnForm, setReturnForm] = useState({
        return_date: "",
        return_remark: ""
    })
    const [isReturnLoading, setIsReturnLoading] = useState(false)

    // ─── History Modal State ─────────────────────────────────────────────────────
    const [historyModal, setHistoryModal] = useState(false)
    const [historyAssetName, setHistoryAssetName] = useState("")
    const [historyData, setHistoryData] = useState([])
    const [historyLoading, setHistoryLoading] = useState(false)

    const toggleViewModal = () => {
        setViewModal(!viewModal)
        if (viewModal) {
            setViewAssetData(null)
        }
    }

    const handleAssetView = async (id) => {
        const res = await get(`${ASSET_DETAILS_URL}/${id}`)
        if (res.status === 200 || res.status === true) {
            setViewAssetData(res.data)
            setViewModal(true)
        } else {
            Notifications(res.message || "Failed to fetch asset details", "error")
        }
    }

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            setAssetForm(prev => ({ ...prev, image: acceptedFiles[0] }))
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: "image/*",
        maxFiles: 1
    })

    // Data States
    const [assetTypeData, setAssetTypeData] = useState({ rows: [], total_rows: 0 })
    const [assetData, setAssetData] = useState({ rows: [], total_rows: 0 })

    const [pagination, setPagination] = useState({
        limit: 10,
        page: 1,
        search: ""
    })

    // Form States
    const [assetTypeForm, setAssetTypeForm] = useState({
        branch_id: "",
        type_name: ""
    })

    const [branchOptions, setBranchOptions] = useState([])
    const [employeeOptions, setEmployeeOptions] = useState([])
    const [assetTypeOptions, setAssetTypeOptions] = useState([])

    const [filters, setFilters] = useState({
        branch_id: "",
        type_id: "",
        name: "",
        employee_id: "",
        purchased_date: ""
    })

    const [typeFilters, setTypeFilters] = useState({
        branch_id: "",
        name: ""
    })

    const [assetForm, setAssetForm] = useState({
        branch_id: "",
        asset_type_id: "",
        name: "",
        asset_code: "",
        asset_serial_number: "",
        purchased_date: "",
        warranty_available: "0",
        warranty_end_date: "",
        employee_available: "0",
        description: "",
        image: null
    })

    const toggleTab = tab => {
        if (activeTab !== tab) setActiveTab(tab)
    }

    const toggleAssetTypeModal = () => {
        setAssetTypeModal(!assetTypeModal)
        if (!assetTypeModal) {
            setEditId(null)
            setAssetTypeForm({ branch_id: "", type_name: "" })
        }
    }

    const toggleAssetModal = () => {
        setAssetModal(!assetModal)
        if (!assetModal) {
            setEditId(null)
            setExistingImage(null)
            setAssetForm({
                branch_id: "",
                asset_type_id: "",
                name: "",
                asset_code: "",
                asset_serial_number: "",
                purchased_date: "",
                warranty_available: "0",
                warranty_end_date: "",
                employee_available: "0",
                description: "",
                image: null
            })
        }
    }

    // ─── Assign Modal Toggles ────────────────────────────────────────────────────
    const toggleAssignModal = () => {
        setAssignModal(!assignModal)
        if (!assignModal) {
            setAssignForm({ employee_id: "", assign_date: "", remark: "" })
            setAssignAssetId(null)
            setAssignAssetName("")
        }
    }

    const handleOpenAssign = (row) => {
        setAssignAssetId(row.id)
        setAssignAssetName(row.name)
        setAssignForm({ employee_id: "", assign_date: "", remark: "" })
        setAssignModal(true)
    }

    const handleAssignSubmit = async (e) => {
        e.preventDefault()
        if (!assignForm.employee_id || !assignForm.assign_date) {
            Notifications("Please select an employee and assign date", "error")
            return
        }
        setIsAssignLoading(true)
        const res = await post(`${ASSET_ASSIGN_URL}/${assignAssetId}`, {
            employee_id: assignForm.employee_id,
            assign_date: assignForm.assign_date,
            remark: assignForm.remark
        })
        setIsAssignLoading(false)
        if (res.status === 200 || res.status === true) {
            Notifications("Asset assigned to employee successfully!")
            toggleAssignModal()
            getData()
        } else {
            Notifications(res.message || "Failed to assign asset", "error")
        }
    }

    // ─── Return Modal Toggles ────────────────────────────────────────────────────
    const toggleReturnModal = () => {
        setReturnModal(!returnModal)
        if (!returnModal) {
            setReturnForm({ return_date: "", return_remark: "" })
            setReturnAssetId(null)
            setReturnAssetName("")
        }
    }

    const handleOpenReturn = (row) => {
        setReturnAssetId(row.id)
        setReturnAssetName(row.name)
        setReturnForm({ return_date: "", return_remark: "" })
        setReturnModal(true)
    }

    const handleReturnSubmit = async (e) => {
        e.preventDefault()
        if (!returnForm.return_date) {
            Notifications("Please select a return date", "error")
            return
        }
        setIsReturnLoading(true)
        const res = await post(`${ASSET_RETURN_URL}/${returnAssetId}`, {
            return_date: returnForm.return_date,
            return_remark: returnForm.return_remark
        })
        setIsReturnLoading(false)
        if (res.status === 200 || res.status === true) {
            Notifications("Asset returned successfully!")
            toggleReturnModal()
            getData()
        } else {
            Notifications(res.message || "Failed to return asset", "error")
        }
    }

    // ─── History Modal Toggles ───────────────────────────────────────────────────
    const toggleHistoryModal = () => {
        setHistoryModal(!historyModal)
        if (historyModal) {
            setHistoryData([])
            setHistoryAssetName("")
        }
    }

    const handleOpenHistory = async (row) => {
        setHistoryAssetName(row.name)
        setHistoryModal(true)
        setHistoryLoading(true)
        const res = await get(`${ASSET_HISTORY_URL}/${row.id}`)
        setHistoryLoading(false)
        if (res.status === true || res.status === 200) {
            setHistoryData(res.data || [])
        } else {
            Notifications("Failed to load history", "error")
        }
    }

    // ─── Data Fetch ──────────────────────────────────────────────────────────────
    const getData = async () => {
        setIsLoading(true)
        if (activeTab === "1") {
            const query = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                search: pagination.search,
                ...(typeFilters.branch_id && { branch_id: typeFilters.branch_id }),
                ...(typeFilters.name && { name: typeFilters.name })
            }).toString()
            const res = await get(`${ASSET_TYPE_URL}?${query}`)
            if (res.status === 200) {
                setAssetTypeData({
                    rows: res.response.rows || [],
                    total_rows: res.response.total_rows || 0
                })
            }
        } else {
            const query = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                search: pagination.search,
                ...(filters.branch_id && { branch_id: filters.branch_id }),
                ...(filters.type_id && { asset_type_id: filters.type_id }),
                ...(filters.name && { name: filters.name }),
                ...(filters.employee_id && { assigned_to: filters.employee_id }),
                ...(filters.purchased_date && { purchased_date: filters.purchased_date })
            }).toString()
            const res = await get(`${ASSET_URL}?${query}`)
            if (res.status === 200) {
                setAssetData({
                    rows: res.response.rows || [],
                    total_rows: res.response.total_rows || 0
                })
            }
        }
        setIsLoading(false)
    }

    const fetchSelectors = async () => {
        const branchRes = await get("api/selectors/getBranchs")
        if (branchRes.status === 200) {
            setBranchOptions(branchRes.response.map(b => ({ value: b.id, label: b.title })))
        }
        const empRes = await post("api/selectors/getEmployees", { branch: "all" })
        if (empRes.status === 200) {
            setEmployeeOptions(empRes.response || [])
        }
        const typeRes = await get(`${ASSET_TYPE_URL}?limit=1000`)
        if (typeRes.status === 200) {
            setAssetTypeOptions((typeRes.response.rows || []).map(t => ({ value: t.id, label: t.type_name })))
        }
    }

    useEffect(() => {
        fetchSelectors()
    }, [])

    useEffect(() => {
        const handler = setTimeout(() => {
            getData()
        }, 300)
        return () => clearTimeout(handler)
    }, [pagination, activeTab, filters, typeFilters])

    const handleAssetTypeSubmit = async (e) => {
        e.preventDefault()
        const url = editId ? `${ASSET_TYPE_URL}/${editId}` : ASSET_TYPE_URL
        const res = editId ? await put(url, assetTypeForm) : await post(url, assetTypeForm)
        if (res.status === 200 || res.status === true) {
            Notifications(editId ? "Asset Type updated successfully!" : "Asset Type created successfully!")
            toggleAssetTypeModal()
            getData()
            fetchSelectors()
        } else {
            Notifications(res.message || "Failed to save asset type", "error")
        }
    }

    const handleAssetSubmit = async (e) => {
        e.preventDefault()
        if (editId) {
            const res = await put(`${ASSET_URL}/${editId}`, {
                branch_id: assetForm.branch_id,
                asset_type_id: assetForm.asset_type_id,
                name: assetForm.name,
                asset_code: assetForm.asset_code,
                asset_serial_number: assetForm.asset_serial_number,
                purchased_date: assetForm.purchased_date,
                warranty_available: assetForm.warranty_available,
                warranty_end_date: assetForm.warranty_end_date,
                employee_available: assetForm.employee_available,
                description: assetForm.description
            })
            if (res.status === 200 || res.status === true) {
                Notifications("Asset updated successfully!")
                toggleAssetModal()
                getData()
            } else {
                Notifications(res.message || "Failed to update asset", "error")
            }
        } else {
            const formData = new FormData()
            formData.append("branch_id", assetForm.branch_id)
            formData.append("asset_type_id", assetForm.asset_type_id)
            formData.append("name", assetForm.name)
            formData.append("asset_code", assetForm.asset_code)
            formData.append("asset_serial_number", assetForm.asset_serial_number)
            formData.append("purchased_date", assetForm.purchased_date)
            formData.append("warranty_available", assetForm.warranty_available)
            formData.append("warranty_end_date", assetForm.warranty_end_date)
            formData.append("employee_available", assetForm.employee_available)
            formData.append("description", assetForm.description)
            if (assetForm.image) {
                formData.append("image", assetForm.image)
            }

            const res = await uploadfile(ASSET_URL, formData)
            if (res.status === 200 || res.status === true) {
                Notifications("Asset created successfully!")
                toggleAssetModal()
                getData()
            } else {
                Notifications(res.message || "Failed to create asset", "error")
            }
        }
    }

    const handleAssetEdit = async (id) => {
        setEditId(id)
        const res = await get(`${ASSET_DETAILS_URL}/${id}`)
        if (res.status === 200 || res.status === true) {
            setAssetForm({
                branch_id: res.data.branch_id || "",
                asset_type_id: res.data.asset_type_id || "",
                name: res.data.name || "",
                asset_code: res.data.asset_code || "",
                asset_serial_number: res.data.asset_serial_number || "",
                purchased_date: res.data.purchased_date || "",
                warranty_available: res.data.warranty_available !== null && res.data.warranty_available !== undefined ? String(res.data.warranty_available) : "0",
                warranty_end_date: res.data.warranty_end_date || "",
                employee_available: res.data.employee_available !== null && res.data.employee_available !== undefined ? String(res.data.employee_available) : "0",
                description: res.data.description || "",
                image: null
            })
            setExistingImage(res.data.image || null)
            setAssetModal(true)
        }
    }

    const handleAssetTypeEdit = async (id) => {
        setEditId(id)
        const res = await get(`${ASSET_TYPE_URL}_details/${id}`)
        if (res.status === 200 || res.status === true) {
            setAssetTypeForm({
                branch_id: res.data.branch_id,
                type_name: res.data.type_name
            })
            setAssetTypeModal(true)
        }
    }

    const assetTypeColumns = [
        {
            dataField: "id",
            text: "#",
            style: { width: 44, textAlign: "center", fontWeight: 700, color: "#b5651d", fontSize: 13 },
            headerStyle: { width: 44, textAlign: "center" },
            formatter: (cell, row, rowIndex) => (pagination.page - 1) * pagination.limit + rowIndex + 1
        },
        {
            dataField: "type_name",
            text: "ASSET TYPE",
            formatter: (cell, row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: "linear-gradient(135deg, #3d1e08 0%, #b5651d 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0
                    }}>
                        <i className="bx bx-category" style={{ fontSize: 16, color: "#fff" }}></i>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#2d2d2d" }}>{cell}</div>
                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>
                            {row.asset_item_count || 0} unit{(row.asset_item_count || 0) !== 1 ? "s" : ""} total
                        </div>
                    </div>
                </div>
            )
        },
        {
            dataField: "asset_item_count",
            text: "INVENTORY",
            formatter: (cell, row) => {
                const total = parseInt(cell) || 0
                const assigned = parseInt(row.assigned_count) || 0
                const available = parseInt(row.available_count) || 0
                const pct = total > 0 ? Math.round((assigned / total) * 100) : 0
                return (
                    <div style={{ minWidth: 160 }}>
                        <div style={{ display: "flex", gap: 8, marginBottom: 5 }}>
                            <span style={{
                                padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                                background: "#3d1e08", color: "#fff"
                            }}>{total} Total</span>
                            <span style={{
                                padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                                background: "#fff3e0", color: "#e65100"
                            }}>{assigned} Assigned</span>
                            <span style={{
                                padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                                background: "#e8f5e9", color: "#2e7d32"
                            }}>{available} Free</span>
                        </div>
                        {total > 0 && (
                            <div style={{ height: 5, background: "#f0ede8", borderRadius: 3, overflow: "hidden" }}>
                                <div style={{
                                    height: "100%", width: `${pct}%`,
                                    background: pct >= 100 ? "#c62828" : pct > 50 ? "#e65100" : "#2e7d32",
                                    borderRadius: 3, transition: "width 0.4s ease"
                                }}></div>
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            dataField: "status",
            text: "STATUS",
            style: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            formatter: (cell) => (
                <div className="form-check form-switch d-flex justify-content-center" dir="ltr">
                    <input type="checkbox" className="form-check-input" defaultChecked={cell === 1 || cell === "1"} />
                </div>
            )
        },
        {
            dataField: "action",
            text: "ACTIONS",
            style: { textAlign: "right", whiteSpace: "nowrap" },
            headerStyle: { textAlign: "right" },
            formatter: (cell, row) => {
                const btnBase = {
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    height: 30, borderRadius: 8, border: "none",
                    cursor: "pointer", fontSize: 13, marginLeft: 4, padding: "0 12px", gap: 5
                }
                return (
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <button
                            title="Manage Assets"
                            onClick={() => {
                                setFilters(prev => ({ ...prev, type_id: row.id }))
                                toggleTab("2")
                            }}
                            style={{ ...btnBase, background: "#3d1e08", color: "#fff", fontWeight: 600, width: "auto" }}
                        >
                            <i className="bx bx-list-ul"></i> Manage Assets
                        </button>
                        <button title="Edit" onClick={() => handleAssetTypeEdit(row.id)}
                            style={{ ...btnBase, background: "#eef2ff", color: "#3f51b5", width: 30, padding: 0 }}>
                            <i className="far fa-edit"></i>
                        </button>
                        <button title="Delete"
                            style={{ ...btnBase, background: "#ffebee", color: "#c62828", width: 30, padding: 0 }}>
                            <i className="far fa-times-circle"></i>
                        </button>
                    </div>
                )
            }
        }
    ]

    const assetColumns = [
        {
            dataField: "id",
            text: "#",
            style: { width: 44, textAlign: "center", fontWeight: 700, color: "#b5651d", fontSize: 13 },
            headerStyle: { width: 44, textAlign: "center" },
            formatter: (cell, row, rowIndex) => (pagination.page - 1) * pagination.limit + rowIndex + 1
        },
        {
            dataField: "image",
            text: "",
            style: { width: 52, textAlign: "center", padding: "6px" },
            headerStyle: { width: 52 },
            formatter: (cell, row) => {
                const src = cell ? `${process.env.REACT_APP_BASEURL || 'http://localhost/dglab/api'}/uploads/assets/${cell}` : null
                return src ? (
                    <img
                        src={src}
                        alt={row.name}
                        style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 8, border: "2px solid #f0ede8" }}
                        onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                    />
                ) : (
                    <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: "linear-gradient(135deg, #ede9e5 0%, #d4c4b0 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <i className="bx bx-cube" style={{ fontSize: 16, color: "#b5651d" }}></i>
                    </div>
                )
            }
        },
        {
            dataField: "name",
            text: "ASSET",
            formatter: (cell, row) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#2d2d2d" }}>{cell}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                        {row.type_name}
                        {row.asset_code && <span style={{ marginLeft: 6, color: "#b5651d" }}>· #{row.asset_code}</span>}
                    </div>
                </div>
            )
        },
        {
            dataField: "asset_serial_number",
            text: "SERIAL NO.",
            formatter: (cell) => cell
                ? <span style={{ fontSize: 12, fontFamily: "monospace", background: "#f5f5f5", padding: "2px 7px", borderRadius: 5, color: "#555" }}>{cell}</span>
                : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>
        },
        {
            dataField: "employee_available",
            text: "AVAILABLE",
            style: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
            formatter: (cell) => (cell === "1" || cell === 1)
                ? <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "#e8f5e9", color: "#2e7d32" }}>Yes</span>
                : <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "#ffebee", color: "#c62828" }}>No</span>
        },
        {
            dataField: "assigned_to_name",
            text: "ASSIGNED TO",
            formatter: (cell, row) => {
                if (cell && row.assigned_to) {
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{
                                width: 30, height: 30, borderRadius: "50%",
                                background: "linear-gradient(135deg, #b5651d, #7e431b)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0
                            }}>
                                <i className="bx bx-user" style={{ fontSize: 14, color: "#fff" }}></i>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 12, color: "#3d1e08" }}>{cell}</div>
                                <div style={{ fontSize: 10, color: "#aaa", marginTop: 1 }}>
                                    <i className="bx bx-calendar" style={{ fontSize: 10, marginRight: 3 }}></i>{row.assign_date}
                                </div>
                            </div>
                        </div>
                    )
                }
                return (
                    <span style={{ padding: "2px 10px", borderRadius: 10, fontSize: 11, background: "#f0f4f8", color: "#aaa" }}>Unassigned</span>
                )
            }
        },
        {
            dataField: "action",
            text: "ACTIONS",
            style: { textAlign: "right", whiteSpace: "nowrap" },
            headerStyle: { textAlign: "right" },
            formatter: (cell, row) => {
                const isAssigned = row.assigned_to && row.assigned_to !== null && row.assigned_to !== ""
                const btnBase = {
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 30, height: 30, borderRadius: 8, border: "none",
                    cursor: "pointer", fontSize: 14, marginLeft: 4
                }
                return (
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, flexWrap: "wrap" }}>
                        <button title="Edit" onClick={() => handleAssetEdit(row.id)}
                            style={{ ...btnBase, background: "#eef2ff", color: "#3f51b5" }}>
                            <i className="far fa-edit"></i>
                        </button>
                        <button title="View Details" onClick={() => handleAssetView(row.id)}
                            style={{ ...btnBase, background: "#e0f7fa", color: "#0097a7" }}>
                            <i className="far fa-eye"></i>
                        </button>
                        {!isAssigned ? (
                            <button title="Assign to Employee" onClick={() => handleOpenAssign(row)}
                                style={{ ...btnBase, background: "#3d1e08", color: "#fff" }}>
                                <i className="bx bx-user-plus"></i>
                            </button>
                        ) : (
                            <button title="Return Asset" onClick={() => handleOpenReturn(row)}
                                style={{ ...btnBase, background: "#fff3e0", color: "#e65100" }}>
                                <i className="bx bx-undo"></i>
                            </button>
                        )}
                        <button title="Assignment History" onClick={() => handleOpenHistory(row)}
                            style={{ ...btnBase, background: "#f3e5f5", color: "#7b1fa2" }}>
                            <i className="bx bx-history"></i>
                        </button>
                        <button title="Delete"
                            style={{ ...btnBase, background: "#ffebee", color: "#c62828" }}>
                            <i className="far fa-times-circle"></i>
                        </button>
                    </div>
                )
            }
        }
    ]

    const brownBtnStyle = { backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Dashboard | Assets</title>
                </MetaTags>
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <Nav tabs className="nav-tabs-custom">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "2" })}
                                            onClick={() => toggleTab("2")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="d-block d-sm-none"><i className="far fa-user"></i></span>
                                            <span className="d-none d-sm-block">Assets</span>
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "1" })}
                                            onClick={() => toggleTab("1")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="d-block d-sm-none"><i className="fas fa-cog"></i></span>
                                            <span className="d-none d-sm-block">Asset Types</span>
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                {activeTab === "1" ? (
                                    <Button style={brownBtnStyle} onClick={toggleAssetTypeModal}>
                                        <i className="fas fa-plus me-1"></i> Add Asset Types
                                    </Button>
                                ) : (
                                    <Button style={brownBtnStyle} onClick={toggleAssetModal}>
                                        <i className="fas fa-plus me-1"></i> Add Asset
                                    </Button>
                                )}
                            </div>

                            <TabContent activeTab={activeTab} className="p-0 text-muted">
                                <TabPane tabId="1">
                                    {/* Filter Panel */}
                                    <div style={{
                                        background: "linear-gradient(135deg, #fffdf9 0%, #fdf6ee 100%)",
                                        border: "1px solid #f0e6d9",
                                        borderRadius: 10,
                                        padding: "16px 20px",
                                        marginBottom: 20
                                    }}>
                                        <div style={{
                                            fontSize: 11, fontWeight: 700, color: "#7e431b",
                                            textTransform: "uppercase", letterSpacing: "1px",
                                            marginBottom: 14, display: "flex", alignItems: "center", gap: 6
                                        }}>
                                            <i className="bx bx-filter-alt"></i> Filter Asset Types
                                        </div>
                                        <Row>
                                            <Col md={4}>
                                                <Select
                                                    placeholder="Select Branch"
                                                    options={branchOptions}
                                                    value={branchOptions.find(b => b.value === typeFilters.branch_id) || null}
                                                    onChange={(e) => setTypeFilters(prev => ({ ...prev, branch_id: e ? e.value : "" }))}
                                                    isClearable
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Input
                                                    type="text"
                                                    placeholder="Type Name"
                                                    value={typeFilters.name}
                                                    onChange={(e) => setTypeFilters(prev => ({ ...prev, name: e.target.value }))}
                                                />
                                            </Col>
                                            <Col md={4} className="d-flex align-items-center">
                                                <button
                                                    onClick={() => setTypeFilters({ branch_id: "", name: "" })}
                                                    style={{
                                                        background: "#f0e6d9", border: "none", borderRadius: 8,
                                                        padding: "7px 18px", fontSize: 13, fontWeight: 600,
                                                        color: "#7e431b", cursor: "pointer", display: "flex",
                                                        alignItems: "center", gap: 6
                                                    }}
                                                >
                                                    <i className="bx bx-reset"></i> Reset
                                                </button>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Info banner */}
                                    <div style={{
                                        background: "linear-gradient(90deg, #3d1e08 0%, #7e431b 100%)",
                                        borderRadius: 10, padding: "12px 18px", marginBottom: 14,
                                        display: "flex", alignItems: "center", gap: 12
                                    }}>
                                        <i className="bx bx-info-circle" style={{ fontSize: 20, color: "rgba(255,255,255,0.8)" }}></i>
                                        <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>
                                            Each asset type can have <strong style={{ color: "#ffd580" }}>multiple individual units</strong>.
                                            Click <strong style={{ color: "#ffd580" }}>Manage Assets</strong> on any type to view &amp; assign its units to employees.
                                        </div>
                                    </div>

                                    {/* List header */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#3d1e08", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                                            Asset Types
                                        </div>
                                        <span style={{
                                            background: "#3d1e08", color: "#fff",
                                            fontSize: 11, fontWeight: 700,
                                            padding: "2px 9px", borderRadius: 20
                                        }}>{assetTypeData.total_rows}</span>
                                    </div>

                                    <Datatables
                                        columns={assetTypeColumns}
                                        rows={assetTypeData.rows}
                                        keyField="id"
                                        rowsLength={assetTypeData.total_rows}
                                        isTableHead={false}
                                        isAdd={false}
                                        isSearch={false}
                                        loading={isLoading}
                                        ssr={setPagination}
                                    />
                                </TabPane>

                                <TabPane tabId="2">
                                    {/* Filter Panel */}
                                    <div style={{
                                        background: "linear-gradient(135deg, #fffdf9 0%, #fdf6ee 100%)",
                                        border: "1px solid #f0e6d9",
                                        borderRadius: 10,
                                        padding: "16px 20px",
                                        marginBottom: 20
                                    }}>
                                        <div style={{
                                            fontSize: 11, fontWeight: 700, color: "#7e431b",
                                            textTransform: "uppercase", letterSpacing: "1px",
                                            marginBottom: 14, display: "flex", alignItems: "center", gap: 6
                                        }}>
                                            <i className="bx bx-filter-alt"></i> Filter Assets
                                        </div>
                                        <Row>
                                            <Col md={3} className="mb-2">
                                                <Select
                                                    placeholder="Select Branch"
                                                    options={branchOptions}
                                                    value={branchOptions.find(b => b.value === filters.branch_id) || null}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, branch_id: e ? e.value : "" }))}
                                                    isClearable
                                                />
                                            </Col>
                                            <Col md={3} className="mb-2">
                                                <Input
                                                    type="text"
                                                    placeholder="Asset name"
                                                    value={filters.name}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                                                />
                                            </Col>
                                            <Col md={3} className="mb-2">
                                                <Select
                                                    placeholder="Asset Type"
                                                    options={assetTypeOptions}
                                                    value={assetTypeOptions.find(t => t.value === filters.type_id) || null}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, type_id: e ? e.value : "" }))}
                                                    isClearable
                                                />
                                            </Col>
                                            <Col md={3} className="mb-2">
                                                <Select
                                                    placeholder="Select Employee"
                                                    options={employeeOptions}
                                                    value={employeeOptions.find(emp => emp.value === filters.employee_id) || null}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, employee_id: e ? e.value : "" }))}
                                                    isClearable
                                                />
                                            </Col>
                                            <Col md={3} className="mb-2">
                                                <Flatpickr
                                                    className="form-control"
                                                    placeholder="Purchased Date"
                                                    value={filters.purchased_date}
                                                    options={{ dateFormat: "Y-m-d" }}
                                                    onChange={(date, dateStr) => setFilters(prev => ({ ...prev, purchased_date: dateStr }))}
                                                />
                                            </Col>
                                            <Col md={3} className="d-flex align-items-center">
                                                <button
                                                    onClick={() => setFilters({ branch_id: "", type_id: "", name: "", employee_id: "", purchased_date: "" })}
                                                    style={{
                                                        background: "#f0e6d9", border: "none", borderRadius: 8,
                                                        padding: "7px 18px", fontSize: 13, fontWeight: 600,
                                                        color: "#7e431b", cursor: "pointer", display: "flex",
                                                        alignItems: "center", gap: 6
                                                    }}
                                                >
                                                    <i className="bx bx-reset"></i> Reset
                                                </button>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Type filter active banner */}
                                    {filters.type_id && (() => {
                                        const activeType = assetTypeOptions.find(t => t.value === filters.type_id)
                                        return activeType ? (
                                            <div style={{
                                                background: "linear-gradient(90deg, #3d1e08 0%, #7e431b 100%)",
                                                borderRadius: 10, padding: "10px 16px", marginBottom: 14,
                                                display: "flex", alignItems: "center", justifyContent: "space-between"
                                            }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <i className="bx bx-filter-alt" style={{ fontSize: 18, color: "#ffd580" }}></i>
                                                    <div>
                                                        <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>Showing assets for type: </span>
                                                        <strong style={{ color: "#ffd580", fontSize: 13 }}>{activeType.label}</strong>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setFilters(prev => ({ ...prev, type_id: "" }))}
                                                    style={{
                                                        background: "rgba(255,255,255,0.15)", border: "none",
                                                        borderRadius: 6, padding: "4px 12px", color: "#fff",
                                                        fontSize: 11, cursor: "pointer", fontWeight: 600,
                                                        display: "flex", alignItems: "center", gap: 4
                                                    }}
                                                >
                                                    <i className="bx bx-x"></i> Clear Filter
                                                </button>
                                            </div>
                                        ) : null
                                    })()}

                                    {/* List Header */}
                                    <div style={{
                                        display: "flex", alignItems: "center",
                                        justifyContent: "space-between", marginBottom: 12
                                    }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <div style={{
                                                fontSize: 13, fontWeight: 700, color: "#3d1e08",
                                                textTransform: "uppercase", letterSpacing: "0.8px"
                                            }}>
                                                Assets List
                                            </div>
                                            <span style={{
                                                background: "#3d1e08", color: "#fff",
                                                fontSize: 11, fontWeight: 700,
                                                padding: "2px 9px", borderRadius: 20
                                            }}>{assetData.total_rows}</span>
                                        </div>
                                        <button
                                            onClick={toggleAssetModal}
                                            style={{
                                                background: "#3d1e08", border: "none", borderRadius: 8,
                                                padding: "7px 16px", fontSize: 12, fontWeight: 600,
                                                color: "#fff", cursor: "pointer", display: "flex",
                                                alignItems: "center", gap: 6
                                            }}
                                        >
                                            <i className="fas fa-plus"></i> Add Asset Unit
                                        </button>
                                    </div>

                                    <Datatables
                                        columns={assetColumns}
                                        rows={assetData.rows}
                                        keyField="id"
                                        rowsLength={assetData.total_rows}
                                        isTableHead={false}
                                        isAdd={false}
                                        isSearch={false}
                                        loading={isLoading}
                                        ssr={setPagination}
                                    />
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* ─── Asset Type Modal ───────────────────────────────────────────────────── */}
            <Modal isOpen={assetTypeModal} toggle={toggleAssetTypeModal} size="lg">
                <ModalHeader toggle={toggleAssetTypeModal}>{editId ? "Edit Asset Type" : "Add Asset Types"}</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleAssetTypeSubmit}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Branch"
                                        options={branchOptions}
                                        value={branchOptions.find(b => b.value == assetTypeForm.branch_id) || null}
                                        onChange={(e) => setAssetTypeForm(prev => ({ ...prev, branch_id: e ? e.value : "" }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Name <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter Assets Type Name"
                                        value={assetTypeForm.type_name}
                                        onChange={(e) => setAssetTypeForm(prev => ({ ...prev, type_name: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <div className="mt-3">
                            <Button style={brownBtnStyle} className="me-2" type="submit">
                                <i className="fas fa-plus me-1"></i> {editId ? "Update" : "Create"}
                            </Button>
                            <Button color="secondary" onClick={toggleAssetTypeModal} style={{ backgroundColor: "#747d8c", borderColor: "#747d8c" }}>Cancel</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* ─── Asset Create/Edit Modal ────────────────────────────────────────────── */}
            <Modal isOpen={assetModal} toggle={toggleAssetModal} size="xl">
                <ModalHeader toggle={toggleAssetModal}>{editId ? "Edit Asset" : "Create Asset"}</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleAssetSubmit}>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Branch"
                                        options={branchOptions}
                                        value={branchOptions.find(b => b.value == assetForm.branch_id) || null}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, branch_id: e ? e.value : "" }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Type <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Asset Type"
                                        options={assetTypeOptions}
                                        value={assetTypeOptions.find(t => t.value == assetForm.asset_type_id) || null}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_type_id: e ? e.value : "" }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Name <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter name"
                                        value={assetForm.name}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Asset Code</Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter Asset Code"
                                        value={assetForm.asset_code}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_code: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Asset Serial Number</Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter Asset Serial Number"
                                        value={assetForm.asset_serial_number}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_serial_number: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Purchased Date <span className="text-danger">*</span></Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ dateFormat: "Y-m-d" }}
                                        placeholder="mm/dd/yyyy"
                                        value={assetForm.purchased_date}
                                        onChange={(date, dateStr) => setAssetForm(prev => ({ ...prev, purchased_date: dateStr }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Warranty Available <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Warranty Availability"
                                        options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
                                        value={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }].find(w => w.value == assetForm.warranty_available) || null}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, warranty_available: e ? e.value : "0" }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Warranty End Date</Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ dateFormat: "Y-m-d" }}
                                        placeholder="mm/dd/yyyy"
                                        value={assetForm.warranty_end_date}
                                        onChange={(date, dateStr) => setAssetForm(prev => ({ ...prev, warranty_end_date: dateStr }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Is Available For Employee <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Select Availability"
                                        options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
                                        value={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }].find(a => a.value == assetForm.employee_available) || null}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, employee_available: e ? e.value : "0" }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Upload Image {editId ? "" : <span className="text-danger">*</span>}</Label>
                                    <div
                                        {...getRootProps()}
                                        className="dropzone d-flex align-items-center justify-content-center border-2 border-dashed rounded p-3 bg-light"
                                        style={{ minHeight: "100px", cursor: "pointer", borderColor: isDragActive ? "#7e431b" : "#ced4da" }}
                                    >
                                        <input {...getInputProps()} />
                                        <div className="text-center">
                                            <i className="display-4 text-muted bx bxs-cloud-upload"></i>
                                            <p className="mt-2 text-muted font-size-13 mb-0">
                                                {isDragActive ? "Drop the file here..." : "Drag & drop an image here, or click to select"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Preview Section */}
                                    {(assetForm.image || existingImage) && (
                                        <div className="mt-3 p-2 border rounded bg-white text-center">
                                            <h6 className="text-muted font-size-12 mb-2">Image Preview</h6>
                                            <img
                                                src={assetForm.image ? URL.createObjectURL(assetForm.image) : `${process.env.REACT_APP_BASEURL || 'http://localhost/dglab/api'}/uploads/assets/${existingImage}`}
                                                alt="Asset Preview"
                                                style={{ maxWidth: "100%", maxHeight: "150px", objectFit: "contain" }}
                                                className="rounded shadow-sm"
                                            />
                                            {assetForm.image && (
                                                <div className="mt-2">
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setAssetForm(prev => ({ ...prev, image: null }))
                                                        }}
                                                    >
                                                        Remove New Image
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Description</Label>
                                    <Input
                                        type="textarea"
                                        rows="5"
                                        value={assetForm.description}
                                        onChange={(e) => setAssetForm(prev => ({ ...prev, description: e.target.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <div className="mt-3">
                            <Button style={brownBtnStyle} type="submit">
                                <i className="fas fa-plus me-1"></i> {editId ? "Update Asset" : "Create Asset"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* ─── View Asset Modal — Modern UI ─────────────────────────────────── */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg">
                <ModalBody className="p-0" style={{ borderRadius: 12, overflow: "hidden" }}>
                    {viewAssetData ? (() => {
                        const isAssigned = viewAssetData.is_assigned == 1 || viewAssetData.is_assigned === true || (viewAssetData.assigned_to && viewAssetData.assigned_to !== null && viewAssetData.assigned_to !== "")
                        const hasWarranty = viewAssetData.warranty_available == "1" || viewAssetData.warranty_available == 1
                        const isAvailable = viewAssetData.employee_available == "1" || viewAssetData.employee_available == 1
                        const imgSrc = viewAssetData.image ? `${process.env.REACT_APP_BASEURL || 'http://localhost/dglab/api'}/uploads/assets/${viewAssetData.image}` : null

                        return (
                            <div>
                                {/* ── Top Hero ── */}
                                <div style={{ position: "relative", background: "#1a1a2e" }}>
                                    {/* Close btn */}
                                    <button onClick={toggleViewModal} style={{
                                        position: "absolute", top: 12, right: 12, zIndex: 10,
                                        width: 32, height: 32, borderRadius: "50%",
                                        background: "rgba(255,255,255,0.18)", border: "none",
                                        color: "#fff", fontSize: 18, cursor: "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        lineHeight: 1
                                    }}>&times;</button>

                                    {/* Image or Placeholder hero */}
                                    <div style={{
                                        height: 200, overflow: "hidden",
                                        background: imgSrc ? "#000" : "linear-gradient(135deg, #3d1e08 0%, #7e431b 60%, #b5651d 100%)",
                                        display: "flex", alignItems: "center", justifyContent: "center"
                                    }}>
                                        {imgSrc ? (
                                            <img
                                                src={imgSrc}
                                                alt={viewAssetData.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }}
                                                onError={(e) => { e.target.parentNode.style.background = "linear-gradient(135deg, #3d1e08 0%, #7e431b 60%, #b5651d 100%)"; e.target.style.display = "none"; }}
                                            />
                                        ) : (
                                            <div style={{ textAlign: "center" }}>
                                                <i className="bx bx-laptop" style={{ fontSize: 64, color: "rgba(255,255,255,0.35)" }}></i>
                                            </div>
                                        )}
                                    </div>

                                    {/* Overlay badge row */}
                                    <div style={{
                                        position: "absolute", bottom: 12, left: 16, right: 52,
                                        display: "flex", alignItems: "flex-end", justifyContent: "space-between"
                                    }}>
                                        <div>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
                                                {viewAssetData.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>
                                                {viewAssetData.type_name}
                                                {viewAssetData.asset_code && <span style={{ marginLeft: 8 }}>&nbsp;&middot;&nbsp; #{viewAssetData.asset_code}</span>}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <span style={{
                                                padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                                background: isAssigned ? "rgba(255,152,0,0.85)" : "rgba(76,175,80,0.85)",
                                                color: "#fff", letterSpacing: "0.3px"
                                            }}>
                                                {isAssigned ? "Assigned" : "Available"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Details Table ── */}
                                <div style={{ padding: "20px 20px 0" }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 700, color: "#7e431b",
                                        textTransform: "uppercase", letterSpacing: "1px",
                                        marginBottom: 10, display: "flex", alignItems: "center", gap: 6
                                    }}>
                                        <i className="bx bx-list-ul"></i> Asset Details
                                    </div>

                                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, borderRadius: 8, overflow: "hidden", border: "1px solid #f0ede8" }}>
                                        <tbody>
                                            {[
                                                { no: 1, label: "Asset Name", icon: "bx-cube", value: viewAssetData.name || "—" },
                                                { no: 2, label: "Asset Type", icon: "bx-category", value: viewAssetData.type_name || "—" },
                                                { no: 3, label: "Branch", icon: "bx-buildings", value: branchOptions.find(b => b.value == viewAssetData.branch_id)?.label || "—" },
                                                { no: 4, label: "Asset Code", icon: "bx-barcode", value: viewAssetData.asset_code || "—" },
                                                { no: 5, label: "Serial Number", icon: "bx-hash", value: viewAssetData.asset_serial_number || "—" },
                                                { no: 6, label: "Purchased Date", icon: "bx-calendar", value: viewAssetData.purchased_date || "—" },
                                                { no: 7, label: "Warranty", icon: "bx-shield", value: hasWarranty ? `Yes — Expires ${viewAssetData.warranty_end_date || "—"}` : "No Warranty" },
                                                { no: 8, label: "Available for Employee", icon: "bx-user-check", value: isAvailable ? "Yes" : "No" },
                                                { no: 9, label: "Description", icon: "bx-note", value: viewAssetData.description || "No description provided.", italic: !viewAssetData.description },
                                            ].map((row, i) => (
                                                <tr key={row.no} style={{ background: i % 2 === 0 ? "#fffdf9" : "#fff" }}>
                                                    <td style={{
                                                        width: 36, padding: "10px 0 10px 14px",
                                                        color: "#b5651d", fontWeight: 700, fontSize: 12,
                                                        borderBottom: "1px solid #f5ede4", verticalAlign: "top"
                                                    }}>{row.no}</td>
                                                    <td style={{
                                                        width: 160, padding: "10px 12px",
                                                        borderBottom: "1px solid #f5ede4", verticalAlign: "top"
                                                    }}>
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "flex", alignItems: "center", gap: 5 }}>
                                                            <i className={`bx ${row.icon}`} style={{ fontSize: 14, color: "#b5651d" }}></i>
                                                            {row.label}
                                                        </span>
                                                    </td>
                                                    <td style={{
                                                        padding: "10px 14px 10px 4px",
                                                        borderBottom: "1px solid #f5ede4",
                                                        fontSize: 13, fontWeight: 600,
                                                        color: row.italic ? "#aaa" : "#2d2d2d",
                                                        fontStyle: row.italic ? "italic" : "normal",
                                                        verticalAlign: "top"
                                                    }}>
                                                        {row.no === 8 ? (
                                                            <span style={{
                                                                padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                                                                background: isAvailable ? "#e8f5e9" : "#ffebee",
                                                                color: isAvailable ? "#2e7d32" : "#c62828"
                                                            }}>{isAvailable ? "Yes" : "No"}</span>
                                                        ) : row.no === 7 ? (
                                                            <span style={{
                                                                padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                                                                background: hasWarranty ? "#e3f2fd" : "#f5f5f5",
                                                                color: hasWarranty ? "#1565c0" : "#888"
                                                            }}>{row.value}</span>
                                                        ) : row.value}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ── Assigned Employee Card ── */}
                                <div style={{ padding: "16px 20px" }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 700, color: "#7e431b",
                                        textTransform: "uppercase", letterSpacing: "1px",
                                        marginBottom: 10, display: "flex", alignItems: "center", gap: 6
                                    }}>
                                        <i className="bx bx-user-pin"></i> Assignment Info
                                    </div>

                                    {isAssigned && viewAssetData.assigned_to_name ? (
                                        <div style={{
                                            border: "1px solid #f5cba7",
                                            borderRadius: 10,
                                            overflow: "hidden"
                                        }}>
                                            {/* Employee header */}
                                            <div style={{
                                                background: "linear-gradient(90deg, #3d1e08 0%, #7e431b 100%)",
                                                padding: "12px 16px",
                                                display: "flex", alignItems: "center", gap: 12
                                            }}>
                                                <div style={{
                                                    width: 40, height: 40, borderRadius: "50%",
                                                    background: "rgba(255,255,255,0.2)",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0
                                                }}>
                                                    <i className="bx bx-user" style={{ fontSize: 20, color: "#fff" }}></i>
                                                </div>
                                                <div>
                                                    <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Assigned To</div>
                                                    <div style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>{viewAssetData.assigned_to_name}</div>
                                                    {viewAssetData.assigned_emp_code && (
                                                        <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 11 }}>Emp Code: {viewAssetData.assigned_emp_code}</div>
                                                    )}
                                                </div>
                                                <div className="ms-auto">
                                                    <span style={{
                                                        padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                                                        background: "rgba(255,200,100,0.25)", color: "#ffd580",
                                                        border: "1px solid rgba(255,200,100,0.3)"
                                                    }}>Currently Assigned</span>
                                                </div>
                                            </div>

                                            {/* Contact details table */}
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <tbody>
                                                    {[
                                                        { label: "Assign Date", icon: "bx-calendar-check", value: viewAssetData.assign_date || "—" },
                                                        {
                                                            label: "Mobile", icon: "bx-phone", value: viewAssetData.assigned_emp_mobile
                                                                ? <a href={`tel:${viewAssetData.assigned_emp_mobile}`} style={{ color: "#7e431b", textDecoration: "none", fontWeight: 600 }}>{viewAssetData.assigned_emp_mobile}</a>
                                                                : <span style={{ color: "#aaa", fontStyle: "italic" }}>Not available</span>
                                                        },
                                                        {
                                                            label: "Email", icon: "bx-envelope", value: viewAssetData.assigned_emp_email
                                                                ? <a href={`mailto:${viewAssetData.assigned_emp_email}`} style={{ color: "#7e431b", textDecoration: "none", fontWeight: 600 }}>{viewAssetData.assigned_emp_email}</a>
                                                                : <span style={{ color: "#aaa", fontStyle: "italic" }}>Not available</span>
                                                        },
                                                        { label: "Remark", icon: "bx-comment-detail", value: viewAssetData.assign_remark || <span style={{ color: "#aaa", fontStyle: "italic" }}>No remark</span> },
                                                    ].map((r, i) => (
                                                        <tr key={i} style={{ background: i % 2 === 0 ? "#fffdf9" : "#fff" }}>
                                                            <td style={{ width: 140, padding: "9px 12px", borderBottom: "1px solid #f5ede4", verticalAlign: "top" }}>
                                                                <span style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "flex", alignItems: "center", gap: 5 }}>
                                                                    <i className={`bx ${r.icon}`} style={{ fontSize: 14, color: "#b5651d" }}></i>
                                                                    {r.label}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #f5ede4", fontSize: 13, color: "#2d2d2d", fontWeight: 500 }}>
                                                                {r.value}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div style={{
                                            border: "1px dashed #e0d5ca", borderRadius: 10,
                                            padding: "20px 16px", textAlign: "center",
                                            background: "#fafaf8"
                                        }}>
                                            <i className="bx bx-user-x" style={{ fontSize: 32, color: "#d4b59a" }}></i>
                                            <p style={{ fontSize: 13, color: "#aaa", marginTop: 8, marginBottom: 0 }}>This asset is not currently assigned to any employee.</p>
                                        </div>
                                    )}
                                </div>

                                {/* ── Footer ── */}
                                <div style={{
                                    borderTop: "1px solid #f5ede4",
                                    padding: "12px 20px",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    background: "#fffdf9"
                                }}>
                                    <Button
                                        onClick={toggleViewModal}
                                        style={{ backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff", fontSize: 13, padding: "6px 22px" }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        )
                    })() : (
                        <div className="text-center p-5">
                            <i className="bx bx-loader bx-spin font-size-24 text-primary"></i>
                            <p className="mt-2 text-muted">Loading details...</p>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* ─── Assign to Employee Modal ─────────────────────────────────────── */}

            {/* ─── Assign to Employee Modal ───────────────────────────────────────────── */}

            <Modal isOpen={assignModal} toggle={toggleAssignModal} size="md">
                <ModalHeader toggle={toggleAssignModal}>
                    <i className="bx bx-user-plus me-2 text-primary"></i>
                    Assign Asset to Employee
                </ModalHeader>
                <ModalBody>
                    {/* Asset Info Banner */}
                    <div className="rounded mb-4 p-3 d-flex align-items-center gap-3"
                        style={{ background: "linear-gradient(135deg, #3d1e08 0%, #7e431b 100%)" }}>
                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 44, height: 44, background: "rgba(255,255,255,0.2)" }}>
                            <i className="bx bx-laptop text-white" style={{ fontSize: 22 }}></i>
                        </div>
                        <div>
                            <div className="text-white fw-semibold" style={{ fontSize: 15 }}>{assignAssetName}</div>
                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>Assigning this asset to an employee</div>
                        </div>
                    </div>

                    <Form onSubmit={handleAssignSubmit}>
                        <FormGroup>
                            <Label>Employee <span className="text-danger">*</span></Label>
                            <Select
                                placeholder="Select Employee"
                                options={employeeOptions}
                                value={employeeOptions.find(e => e.value === assignForm.employee_id) || null}
                                onChange={(e) => setAssignForm(prev => ({ ...prev, employee_id: e ? e.value : "" }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Assign Date <span className="text-danger">*</span></Label>
                            <Flatpickr
                                className="form-control"
                                options={{ dateFormat: "Y-m-d" }}
                                placeholder="Select assign date"
                                value={assignForm.assign_date}
                                onChange={(date, dateStr) => setAssignForm(prev => ({ ...prev, assign_date: dateStr }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Remark / Notes</Label>
                            <Input
                                type="textarea"
                                rows={3}
                                placeholder="Add any notes or reason for assignment..."
                                value={assignForm.remark}
                                onChange={(e) => setAssignForm(prev => ({ ...prev, remark: e.target.value }))}
                            />
                        </FormGroup>
                        <div className="d-flex gap-2 mt-3">
                            <Button
                                type="submit"
                                disabled={isAssignLoading}
                                style={{ backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }}
                            >
                                {isAssignLoading
                                    ? <><i className="bx bx-loader bx-spin me-1"></i> Assigning...</>
                                    : <><i className="bx bx-user-check me-1"></i> Assign Asset</>}
                            </Button>
                            <Button color="secondary" type="button" onClick={toggleAssignModal}>Cancel</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* ─── Return Asset Modal ─────────────────────────────────────────────────── */}
            <Modal isOpen={returnModal} toggle={toggleReturnModal} size="md">
                <ModalHeader toggle={toggleReturnModal}>
                    <i className="bx bx-undo me-2 text-warning"></i>
                    Return Asset
                </ModalHeader>
                <ModalBody>
                    {/* Asset Info Banner */}
                    <div className="rounded mb-4 p-3 d-flex align-items-center gap-3"
                        style={{ background: "linear-gradient(135deg, #e65100 0%, #ff9800 100%)" }}>
                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 44, height: 44, background: "rgba(255,255,255,0.2)" }}>
                            <i className="bx bx-laptop text-white" style={{ fontSize: 22 }}></i>
                        </div>
                        <div>
                            <div className="text-white fw-semibold" style={{ fontSize: 15 }}>{returnAssetName}</div>
                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>Collecting this asset back from employee</div>
                        </div>
                    </div>

                    <Form onSubmit={handleReturnSubmit}>
                        <FormGroup>
                            <Label>Return Date <span className="text-danger">*</span></Label>
                            <Flatpickr
                                className="form-control"
                                options={{ dateFormat: "Y-m-d" }}
                                placeholder="Select return date"
                                value={returnForm.return_date}
                                onChange={(date, dateStr) => setReturnForm(prev => ({ ...prev, return_date: dateStr }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Return Remark / Condition Notes</Label>
                            <Input
                                type="textarea"
                                rows={3}
                                placeholder="Describe the condition of the asset on return, or reason..."
                                value={returnForm.return_remark}
                                onChange={(e) => setReturnForm(prev => ({ ...prev, return_remark: e.target.value }))}
                            />
                        </FormGroup>
                        <div className="d-flex gap-2 mt-3">
                            <Button
                                type="submit"
                                color="warning"
                                disabled={isReturnLoading}
                            >
                                {isReturnLoading
                                    ? <><i className="bx bx-loader bx-spin me-1"></i> Processing...</>
                                    : <><i className="bx bx-undo me-1"></i> Confirm Return</>}
                            </Button>
                            <Button color="secondary" type="button" onClick={toggleReturnModal}>Cancel</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            {/* ─── Assignment History Modal ───────────────────────────────────────────── */}
            <Modal isOpen={historyModal} toggle={toggleHistoryModal} size="lg">
                <ModalHeader toggle={toggleHistoryModal}>
                    <i className="bx bx-history me-2"></i>
                    Assignment History — {historyAssetName}
                </ModalHeader>
                <ModalBody>
                    {historyLoading ? (
                        <div className="text-center p-4">
                            <i className="bx bx-loader bx-spin font-size-24 text-primary"></i>
                            <p className="mt-2">Loading history...</p>
                        </div>
                    ) : historyData.length === 0 ? (
                        <div className="text-center p-4">
                            <i className="bx bx-inbox font-size-36 text-muted"></i>
                            <p className="mt-2 text-muted">No assignment history found for this asset.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table bordered hover size="sm" className="align-middle">
                                <thead style={{ backgroundColor: "#3d1e08", color: "#fff" }}>
                                    <tr>
                                        <th>#</th>
                                        <th>Employee</th>
                                        <th>Assigned Date</th>
                                        <th>Remark</th>
                                        <th>Return Date</th>
                                        <th>Return Remark</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyData.map((h, idx) => (
                                        <tr key={h.id}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <div className="fw-semibold">{h.employee_name || "—"}</div>
                                                {h.emp_code && <small className="text-muted">{h.emp_code}</small>}
                                            </td>
                                            <td>{h.assign_date || "—"}</td>
                                            <td>
                                                <small className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                                    {h.remark || "—"}
                                                </small>
                                            </td>
                                            <td>{h.return_date || <span className="text-muted">—</span>}</td>
                                            <td>
                                                <small className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                                    {h.return_remark || "—"}
                                                </small>
                                            </td>
                                            <td>
                                                {h.status === "assigned"
                                                    ? <Badge color="success">Assigned</Badge>
                                                    : <Badge color="secondary">Returned</Badge>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </ModalBody>
            </Modal>

        </React.Fragment>
    )
}

export default Assets
